import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../components/common/Button';
import { apiService } from '../services/api';

// Generic placeholder page component
function PlaceholderPage({ title, description }) {
  return (
    <main id="main-content" className="pt-32 pb-20 min-h-screen" style={{ paddingTop: 'calc(var(--alert-banner-height, 0px) + 8rem)' }}>
      <div className="container-narrow text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-serif text-4xl md:text-5xl text-primary mb-6">
            {title}
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {description}
          </p>
          <Button to="/" variant="secondary">
            Back to Home
          </Button>
        </motion.div>
      </div>
    </main>
  );
}

// About Page
export function About() {
  return (
    <PlaceholderPage
      title="About CGS"
      description="Learn about our history, mission, and the people who make up The Church Gathered & Scattered."
    />
  );
}

// Sermons Page
export function Sermons() {
  return (
    <PlaceholderPage
      title="Sermons"
      description="Listen to our sermon archive and dive deeper into God's Word with our teaching ministry."
    />
  );
}

// Events Page
export function Events() {
  return (
    <PlaceholderPage
      title="Events"
      description="View upcoming events, retreats, and gatherings at CGS."
    />
  );
}

// Contact Page
export function Contact() {
  return (
    <PlaceholderPage
      title="Contact Us"
      description="We'd love to hear from you. Reach out with any questions or prayer requests."
    />
  );
}

// Give Page
export function Give() {
  return (
    <PlaceholderPage
      title="Give"
      description="Support the ministry of CGS through your generous giving."
    />
  );
}

// 404 Page - checks for redirects before showing 404
export function NotFound() {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const checkedRef = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-fire
    if (checkedRef.current) return;
    checkedRef.current = true;

    const checkForRedirect = async () => {
      try {
        const response = await apiService.checkRedirect(location.pathname);
        if (response.data) {
          window.location.href = response.data.targetUrl;
          return;
        }
      } catch (err) {
        // Redirect check failed, show 404
      }
      setChecking(false);
    };

    checkForRedirect();
  }, [location.pathname]);

  if (checking) {
    return (
      <main id="main-content" className="pt-32 pb-20 min-h-screen" style={{ paddingTop: 'calc(var(--alert-banner-height, 0px) + 8rem)' }}>
        <div className="container-narrow text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" className="pt-32 pb-20 min-h-screen bg-white" style={{ marginTop: 'calc(var(--alert-banner-height, 0px) + 5rem)' }}>
      <div className="container-narrow text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-8xl font-serif text-primary/20 mb-4">404</p>
          <h1 className="font-serif text-4xl text-primary mb-4">
            Page Not Found
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            The page you're looking for doesn't exist or has been moved.
          </p>
          <Button to="/">
            Return Home
          </Button>
        </motion.div>
      </div>
    </main>
  );
}
